// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import {
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Group,
  List,
  Modal,
  Stack,
  Text,
  ThemeIcon,
} from '@mantine/core';
import type { Organization } from '@medplum/fhirtypes';
import { IconAlertTriangle, IconArrowRight, IconFileText } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useAutoSave } from '../contexts/AutoSaveContext';

interface PracticeSwitchModalProps {
  opened: boolean;
  currentOrganization: Organization | null;
  targetOrganization: Organization;
  onConfirm: () => void;
  onCancel: () => void;
}

export function PracticeSwitchModal({
  opened,
  currentOrganization,
  targetOrganization,
  onConfirm,
  onCancel,
}: PracticeSwitchModalProps): JSX.Element {
  const { drafts, hasUnsavedChanges, clearAllDrafts } = useAutoSave();

  const handleConfirm = (): void => {
    // Clear all drafts when switching
    clearAllDrafts();
    onConfirm();
  };

  const formatTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onCancel}
      title={
        <Group gap="sm">
          <ThemeIcon color="orange" variant="light" size="lg">
            <IconAlertTriangle size={20} />
          </ThemeIcon>
          <Text fw={600} size="lg">
            Switch Practice
          </Text>
        </Group>
      }
      size="md"
      centered
    >
      <Stack gap="md">
        {/* Practice Switch Visual */}
        <Box p="md" style={{ background: 'var(--mantine-color-gray-0)', borderRadius: 8 }}>
          <Group justify="space-between" align="center">
            <Group gap="sm">
              <Avatar size={40} radius="xl" color="gray">
                {currentOrganization?.name?.charAt(0) || 'C'}
              </Avatar>
              <Box>
                <Text size="sm" c="dimmed">
                  Current
                </Text>
                <Text fw={500}>{currentOrganization?.name || 'No Practice'}</Text>
              </Box>
            </Group>

            <IconArrowRight size={24} color="var(--mantine-color-gray-5)" />

            <Group gap="sm">
              <Avatar size={40} radius="xl" color="teal">
                {targetOrganization.name?.charAt(0) || 'N'}
              </Avatar>
              <Box>
                <Text size="sm" c="dimmed">
                  Switching to
                </Text>
                <Text fw={500} c="teal">
                  {targetOrganization.name}
                </Text>
              </Box>
            </Group>
          </Group>
        </Box>

        <Divider />

        {/* Warning about unsaved changes */}
        {hasUnsavedChanges && (
          <Box
            p="md"
            style={{
              background: 'var(--mantine-color-orange-0)',
              borderRadius: 8,
              border: '1px solid var(--mantine-color-orange-3)',
            }}
          >
            <Group gap="sm" mb="xs">
              <IconAlertTriangle size={18} color="var(--mantine-color-orange-6)" />
              <Text fw={600} c="orange.7" size="sm">
                Unsaved Changes Will Be Lost
              </Text>
            </Group>
            <Text size="sm" c="dimmed" mb="sm">
              The following drafts will be discarded when you switch practices:
            </Text>
            <List size="sm" spacing="xs" icon={<IconFileText size={14} />}>
              {drafts.slice(0, 5).map((draft) => (
                <List.Item key={draft.id}>
                  <Group gap="xs">
                    <Text size="sm">
                      {draft.description || `${draft.resourceType} draft`}
                    </Text>
                    <Badge size="xs" variant="light" color="gray">
                      {formatTimestamp(draft.savedAt)}
                    </Badge>
                  </Group>
                </List.Item>
              ))}
              {drafts.length > 5 && (
                <List.Item>
                  <Text size="sm" c="dimmed">
                    ...and {drafts.length - 5} more
                  </Text>
                </List.Item>
              )}
            </List>
          </Box>
        )}

        {!hasUnsavedChanges && (
          <Text size="sm" c="dimmed">
            You are about to switch to a different practice. All data you see will be filtered to
            show only resources belonging to <strong>{targetOrganization.name}</strong>.
          </Text>
        )}

        <Divider />

        {/* Actions */}
        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" color="gray" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            color={hasUnsavedChanges ? 'orange' : 'teal'}
            onClick={handleConfirm}
          >
            {hasUnsavedChanges ? 'Discard & Switch' : 'Switch Practice'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
